import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function TowerHamletsCouncilPage() {
  const faqItems = [
    {
      question: 'Tower Hamlets spends fifty times its DfT capital allocation — can I still claim?',
      answer: 'Yes. The DfT Spend scorecard is GREEN — projected capital spend of £6.9m in 2025/26 against a £137,000 DfT allocation, with 78% classed as preventative. But the DfT Condition scorecard is AMBER, and Tower Hamlets\' own walked surveys show B, C and U-road condition declining since 2021. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
    },
    {
      question: 'What if my pothole was on a residential street in Tower Hamlets?',
      answer: 'U-roads make up 154.4km — 70.3% of the borough\'s 219.6km carriageway network. At the 2024 walked survey, 10.8% of U-roads were rated Poor or Very Poor (maintenance required), up from 6.1% in 2021. Since 2023, B and C roads and U-roads are condition-surveyed every two years by walked engineering inspection — so there may be no network-level survey reading for your road class in alternate years.',
    },
    {
      question: 'Pothole repairs fell 61% in 2024/25 — does that mean the roads are fixed?',
      answer: 'No. Tower Hamlets filled an estimated 307 potholes in 2024/25, down from 777 in 2023/24 — while the council\'s own charts show B, C and U-road condition declining since 2021. The council expects to fix approximately 600 potholes in 2025/26 based on historical trends. Reactive counts fluctuate year to year; structural condition data tells the longer story.',
    },
    {
      question: 'Does the 20% construction-proximity admission help my claim?',
      answer: 'It can. Tower Hamlets states that 20% of its highway network is currently within 100 metres of ongoing construction and development, causing additional damage through increased construction traffic and utility openings. That is documented knowledge of elevated wear on specific corridors — which raises the bar for what a "reasonable" inspection regime looks like on affected routes under Section 58.',
    },
    {
      question: 'A-roads are surveyed by TfL AI — does that weaken claims on main roads?',
      answer: 'You should expect a stronger Section 58 defence on A-roads. TfL\'s AI-led surveys show approximately 20% of Tower Hamlets A-roads in Poor or Very Poor condition since 2022 — below the London average of 29%. But your claim still turns on whether the specific defect was identified and repaired within reasonable timeframes, not on borough-wide averages. No A-road surveys were undertaken in 2020 or 2021 due to Covid-19 restrictions.',
    },
    {
      question: 'Does rising resident satisfaction weaken my pothole claim?',
      answer: 'Not directly. Tower Hamlets reports resident satisfaction with road and pavement maintenance rose 29 percentage points since 2021, reaching 67% — which the council attributes to visible investment. Perception surveys and structural condition data measure different things. The same report shows walked-survey condition on B, C and U-roads declining since 2021. Your claim lives on the specific defect, prior reports and photos — not on satisfaction scores.',
    },
    {
      question: 'How do I report a pothole to Tower Hamlets before claiming?',
      answer: 'Tower Hamlets accepts reports of potholes and other street problems through its online "Report a street problem" form — including location details and a description of the defect. You do not need to log in, though an account lets you track progress. Prior reports through the council\'s system or FixMyStreet are direct evidence of actual notice, which matters when the council relies on Section 58 of the Highways Act 1980.',
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Tower Hamlets Pothole Claims | 20% of Roads Beside Building Sites | Fixtyer"
        description="Tower Hamlets Council: AMBER DfT rating despite GREEN spend. 20% of the network within 100m of construction, U-road Poor/Very Poor share up since 2021, and pothole fills fell 61% in 2024/25. See what this means for your claim."
        canonical="/councils/tower-hamlets"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Tower Hamlets', url: '/councils/tower-hamlets' },
        ]}
      />
      <FAQSchema faqs={faqItems} />
      <ServiceSchema
        councilName="Tower Hamlets Council"
        councilSlug="tower-hamlets"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/tower-hamlets"
        headline="Tower Hamlets Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Tower Hamlets</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            One in Five Tower Hamlets Roads Sits Beside a Building Site
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Tower Hamlets earns a <strong className="text-emerald-400">GREEN</strong> spend scorecard — projecting
            £6.9m capital spend against just £137,000 from the DfT — yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> because the council admits{' '}
            <strong className="text-rose-400">20% of its 219.6km network is within 100 metres of active construction</strong>,
            walked-survey condition on B, C and U-roads has{' '}
            <strong className="text-amber-400">declined since 2021</strong>, and pothole fills{' '}
            <strong className="text-rose-400">fell 61% to 307 in 2024/25</strong> while structural data kept slipping.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">20%</div>
            <div className="text-white font-medium mt-2">Of the network within 100m of construction</div>
            <div className="text-brand-300 text-sm mt-1">
              Tower Hamlets' own report links ongoing development traffic and utility openings to additional road
              damage — on England's most densely populated borough, where 154.4km of residential U-roads make up
              70% of the carriageway network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A Compact, High-Density Network
          </h2>
          <p className="text-surface-600 mb-8">
            Tower Hamlets' own transparency report — 219.6km of carriageways in England's most densely populated borough
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">219.6km</div>
              <div className="text-sm text-surface-600 mt-1">Total carriageways</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">154.4km</div>
              <div className="text-sm text-surface-600 mt-1">U-roads (70.3% of network)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">454.9km</div>
              <div className="text-sm text-surface-600 mt-1">Footways maintained</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">13.8km</div>
              <div className="text-sm text-surface-600 mt-1">A-roads (TfL AI-surveyed)</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Two Survey Systems</h3>
              <p className="text-surface-600 text-sm mb-3">
                A-roads on the Borough Principal Road Network are assessed by Transport for London using AI-led
                video surveys — introduced in 2022. B and C roads (51.4km) and U-roads (154.4km) are surveyed
                by independent consultants using walked engineering inspections.
              </p>
              <p className="text-surface-600 text-sm">
                Since 2023, walked surveys on local classified and unclassified roads happen every two years.
                Prior to 2022, they were undertaken every four years.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Beyond Carriageways</h3>
              <p className="text-amber-800 text-sm">
                Tower Hamlets also maintains 69.7km of cycleways, 56 highway structures, 10,033 street lighting
                columns and 10,848 gullies — all subject to the risk-based hierarchy ratings and inspection
                regimes described in the council's July 2025 report.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Survey data from Tower Hamlets' July 2025 transparency report — A-roads holding steady, local roads slipping
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">
              A-roads (13.8km — 6.3% of network): ~20% Poor or Very Poor, below London average
            </h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Very Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-600">Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Fair</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Good</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-800">Very Good</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={5}>Survey not carried out (Covid-19)</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={5}>Survey not carried out (Covid-19)</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">7.1%</td>
                    <td className="px-4 py-2 text-center">9.3%</td>
                    <td className="px-4 py-2 text-center">24.1%</td>
                    <td className="px-4 py-2 text-center">18.9%</td>
                    <td className="px-4 py-2 text-center">40.6%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6.6%</td>
                    <td className="px-4 py-2 text-center">14.4%</td>
                    <td className="px-4 py-2 text-center">26.9%</td>
                    <td className="px-4 py-2 text-center">19.0%</td>
                    <td className="px-4 py-2 text-center">33.1%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">11.3%</td>
                    <td className="px-4 py-2 text-center font-bold">21.6%</td>
                    <td className="px-4 py-2 text-center font-bold">25.7%</td>
                    <td className="px-4 py-2 text-center font-bold">33.8%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              The council states average Poor and Very Poor A-roads have remained at approximately 20% since 2022 —
              lower than the London average of 29%. Credit where due on principal routes. But A-roads are just
              13.8km of a 219.6km network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">
              B and C roads (51.4km — 23.4% of network): declining since 2021
            </h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Very Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-600">Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Fair</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Good</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-800">Very Good</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">0.2%</td>
                    <td className="px-4 py-2 text-center">9.0%</td>
                    <td className="px-4 py-2 text-center">22.9%</td>
                    <td className="px-4 py-2 text-center">33.1%</td>
                    <td className="px-4 py-2 text-center">34.8%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">1.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">10.2%</td>
                    <td className="px-4 py-2 text-center font-bold">23.9%</td>
                    <td className="px-4 py-2 text-center font-bold">29.5%</td>
                    <td className="px-4 py-2 text-center font-bold">34.8%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Poor or Very Poor B/C roads rose from 9.2% to 11.8%, and Good plus Very Good fell from 68.0% to 64.3%.
              The council's own report states that since 2021, overall condition on classified non-principal roads
              has declined.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">
              U-roads (154.4km — 70.3% of network): Poor and Very Poor share rising
            </h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Very Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-600">Poor</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Fair</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Good</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-800">Very Good</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">0.4%</td>
                    <td className="px-4 py-2 text-center">5.7%</td>
                    <td className="px-4 py-2 text-center">21.3%</td>
                    <td className="px-4 py-2 text-center">27.6%</td>
                    <td className="px-4 py-2 text-center">45.0%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">1.8%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9.0%</td>
                    <td className="px-4 py-2 text-center font-bold">20.2%</td>
                    <td className="px-4 py-2 text-center font-bold">30.1%</td>
                    <td className="px-4 py-2 text-center font-bold">39.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              Poor or Very Poor U-roads climbed from 6.1% to 10.8% — roughly 16.7km of residential streets where
              the council defines maintenance as required. Very Good share fell from 45.0% to 39.0%. This is where
              most pothole claims originate.
            </p>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Since 2021, the overall condition of our classified non-principal (Figure 3) and unclassified roads (Figure 4) has declined. This highlights the need for additional highway maintenance to improve the overall condition of our roads."
              source="London Borough of Tower Hamlets Local Highways Maintenance Transparency Report (July 2025)"
            />
            <QuoteCard
              quote="Poor: Moderate to severe deterioration, maintenance will be required. Very Poor: Severe deterioration, maintenance will be required."
              source="London Borough of Tower Hamlets Local Highways Maintenance Transparency Report (July 2025)"
            />
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — AMBER Condition</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£137k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£6.9m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">78%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Tower Hamlets projects spending more than <span className="text-emerald-400">fifty times</span> its
              DfT capital allocation — with £3.379m revenue on top — yet walked-survey condition on local roads
              is still declining. The DfT Condition scorecard is <span className="text-amber-400">AMBER</span>.
              Money is flowing. Measured road condition on B, C and U-roads is not keeping pace.
            </p>
          </div>
        </div>
      </section>

      {/* The 154km Residential Network */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 154.4km Where Most Claims Happen
          </h2>
          <p className="text-surface-600 mb-8">
            Seven in ten carriageway kilometres are unclassified residential roads — walked surveys every two years since 2023
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Survey method</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A-roads (Principal)</td>
                  <td className="px-6 py-4 text-center font-mono">13.8</td>
                  <td className="px-6 py-4 text-center">6.3%</td>
                  <td className="px-6 py-4 text-center">TfL AI survey (annual)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">51.4</td>
                  <td className="px-6 py-4 text-center">23.4%</td>
                  <td className="px-6 py-4 text-center">Walked engineering survey</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">U-roads (Unclassified)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">154.4</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">70.3%</td>
                  <td className="px-6 py-4 text-center">Walked engineering survey</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Biennial Walked Survey Gap</h3>
              <p className="text-surface-600 text-sm mb-3">
                Since 2023, walked condition surveys on B, C and U-roads happen every two years. Prior to 2022,
                the cycle was every four years. The council notes that recorded condition is a snapshot in time
                and can vary along a road's length — including at bus stops and utility repair patches.
              </p>
              <p className="text-surface-600 text-sm">
                If your incident fell in a year without a walked survey for your road class, the council cannot
                point to contemporaneous network-level condition data — though safety inspections and public
                reports may still apply.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Risk-Based Hierarchy</h3>
              <p className="text-amber-800 text-sm">
                Tower Hamlets assigns each highway section a hierarchy risk rating based on vehicular, pedestrian
                and cyclist volumes, transport hubs, emergency access, schools and event venues. That rating
                determines inspection frequency and defect response times — meaning quieter residential streets
                may receive less frequent planned attention than main corridors.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Tower Hamlets' residential network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your U-road last walked-surveyed — and was it a survey year?</li>
              <li>• If Poor or Very Poor U-road share rose from 6.1% to 10.8%, what was done about your street?</li>
              <li>• Does your road's hierarchy rating explain why a visible defect was not prioritised?</li>
              <li>• Were there prior public reports of the same defect before your incident?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of 154.4km of residential roads from a biennial walked
              snapshot — especially where its own charts show condition sliding.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2,432 Potholes in Four Years — Then a 61% Drop
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled from Tower Hamlets' own transparency report — reactive counts vs structural condition
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimated)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">653</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">695</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">777</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">307</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,432</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2024/25 Plunge</h3>
              <p className="text-surface-600 text-sm">
                Pothole fills fell from 777 to 307 — a 61% drop — while walked-survey condition on B, C and
                U-roads continued to decline. The council expects to fix approximately 600 potholes in 2025/26
                based on historical trends. Reactive repair volume and structural condition are not telling
                the same story.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Risk-Based Pothole Response</h3>
              <p className="text-amber-800 text-sm">
                Tower Hamlets applies a risk-based approach to assess pothole severity and impact, then prioritises
                repairs accordingly. Not every depression in the road surface receives the same response time —
                which makes prior reports, photos showing defect age, and proof the pothole met intervention
                thresholds critical for your claim.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Construction and density pressures */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Construction, Density and Heavier Traffic
          </h2>
          <p className="text-surface-600 mb-8">
            Tower Hamlets' own explanation for accelerated wear — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Construction & Utility Openings: 20% of our highway network is currently within 100 metres of on-going construction and development. This causes additional damage on our roads through increased construction traffic and utility openings to connect services."
              source="London Borough of Tower Hamlets Local Highways Maintenance Transparency Report (July 2025)"
            />
            <QuoteCard
              quote="Buses & Electric Vehicles (EVs): In recent years, there has been a significant increase of the number of electric vehicles and buses. They are significantly heavier than their non-electric equivalents, which puts more stress on road surfaces and substructures."
              source="London Borough of Tower Hamlets Local Highways Maintenance Transparency Report (July 2025)"
            />
            <QuoteCard
              quote="Tower Hamlets has the highest population density out of all the Local Authorities in England. As a result, we face several growing challenges which impact the condition of our highway network, highlighting the need for on-going investment."
              source="London Borough of Tower Hamlets Local Highways Maintenance Transparency Report (July 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Monthly Streetworks Coordination</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council holds monthly coordination meetings with statutory undertakers, highway contractors
                and private developers — using a forward works pipeline mapping tool to plan activities and
                manage disruption. Resurfacing works may be brought forward or delayed when utility works clash.
              </p>
              <p className="text-surface-600 text-sm">
                If your pothole formed after utility excavations or near an active development site, reinstatement
                quality and coordination records may be relevant to your claim.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road within 100 metres of ongoing construction when the defect formed?</li>
                <li>• Did utility openings precede the pothole — and was reinstatement inspected?</li>
                <li>• Did the council increase inspection frequency on construction-affected corridors?</li>
                <li>• Is the defect on a bus route subject to heavier EV and bus loading?</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">2025/26 Planned Programme</h3>
            <p className="text-brand-200 mb-4">
              Tower Hamlets' forward plan for 2025/26, from the council's July 2025 report:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Resurfacing 11.2 kilometres of roads</li>
              <li>• Reconstructing 8.0 kilometres of footways</li>
              <li>• Conducting 20 principal and general bridge and structure inspections</li>
              <li>• Upgrading and replacing 600 street lighting columns</li>
              <li>• Expecting to fix approximately 600 potholes based on historical trends</li>
            </ul>
            <p className="text-brand-200 text-sm">
              78% of the highways budget is planned for preventative works; 22% for reactive defect response.
              On a 219.6km network where walked surveys show local roads declining, 11.2km of resurfacing is
              selective — not comprehensive.
            </p>
          </div>
        </div>
      </section>

      {/* Five years of spending */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital and revenue figures from Tower Hamlets' transparency report — DfT allocation vs actual spend
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£,000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Resurfaced (km)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£8,238</td>
                  <td className="px-4 py-3 text-center">£2,707</td>
                  <td className="px-4 py-3 text-center">10.1</td>
                  <td className="px-4 py-3 text-center">84%</td>
                  <td className="px-4 py-3 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£10,408</td>
                  <td className="px-4 py-3 text-center">£2,973</td>
                  <td className="px-4 py-3 text-center">7.3</td>
                  <td className="px-4 py-3 text-center">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£7,804</td>
                  <td className="px-4 py-3 text-center">£3,808</td>
                  <td className="px-4 py-3 text-center">9.9</td>
                  <td className="px-4 py-3 text-center">76%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£5,369</td>
                  <td className="px-4 py-3 text-center">£3,529</td>
                  <td className="px-4 py-3 text-center">5.8</td>
                  <td className="px-4 py-3 text-center">75%</td>
                  <td className="px-4 py-3 text-center">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£137</td>
                  <td className="px-4 py-3 text-center">£7,074</td>
                  <td className="px-4 py-3 text-center">£3,467</td>
                  <td className="px-4 py-3 text-center">11.0</td>
                  <td className="px-4 py-3 text-center">78%</td>
                  <td className="px-4 py-3 text-center">22%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26</td>
                  <td className="px-4 py-3 text-center font-bold">£137</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£6,900</td>
                  <td className="px-4 py-3 text-center">£3,379</td>
                  <td className="px-4 py-3 text-center font-bold">11.2</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">78%</td>
                  <td className="px-4 py-3 text-center">22%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Since 2021, the number of residents satisfied with the service has increased by 29%, from 2021 to 67%, demonstrating the positive impacts of sustained highway investment and the visible improvement in roads across the borough."
            source="London Borough of Tower Hamlets Local Highways Maintenance Transparency Report (July 2025)"
          />
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Tower Hamlets invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend far exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 75–86% of spend classed as preventative across five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road Poor/Very Poor share ~20% — below London's 29% average
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented risk-based asset management aligned to the Code of Practice
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Resident satisfaction with maintenance rose to 67%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Warm mix asphalt and carbon management strategy in place
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on A-roads and recently resurfaced routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B, C and U-road walked-survey data declining since 2021
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road Poor/Very Poor share up from 6.1% to 10.8% (2021 to 2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 70.3% of network on U-roads surveyed only every two years since 2023
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 20% of network within 100m of construction — council's own wear admission
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2,432 potholes filled in four years — defects still forming reactively
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice scorecard — investment modelling and benchmarking still developing
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a documented hierarchy-based inspection regime, your claim
              lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, Tower Hamlets street problem reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, cite the 10.8% Poor/Very Poor rate and biennial survey gap</li>
              <li>• Proximity to construction sites or recent utility excavations within 100 metres</li>
              <li>• Whether your incident fell between walked survey years for B, C or U-roads</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Tower Hamlets' own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report The Pothole Before You Claim
          </h2>
          <p className="text-surface-600 mb-6">
            Tower Hamlets identifies and prioritises pothole repairs using a risk-based approach — and public
            reports are part of how defects enter the system. If you have not already reported the pothole that
            damaged your vehicle, do so now. A prior report timestamped before your incident is among the
            strongest evidence of actual notice for a Section 58 rebuttal.
          </p>
          <a
            href="https://www.towerhamlets.gov.uk/lgnl/transport_and_streets/roads,_highways_and_pavements/Report_a_street_problem.aspx"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a street problem to Tower Hamlets
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Includes potholes, footway hazards, flooding and faulty street lights. Login optional — required only to track report progress.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Tower Hamlets?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No biennial survey-gap argument</li>
                <li>• No construction-proximity analysis</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road decline since 2021 documented</li>
                <li>✅ 20% construction-proximity admission cited</li>
                <li>✅ 2,432 potholes in four years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Tower Hamlets</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=tower-hamlets"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Tower Hamlets Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqItems.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.towerhamlets.gov.uk/Documents/LB-Tower-Hamlets-Highway-Maintenance-Transparency-Report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              London Borough of Tower Hamlets Local Highways Maintenance Transparency Report (July 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
